import { validateItemForApprove } from '../../fileUpload/utils/validation';

export const SLIDE_STATUS = {
    incomplete: 'incomplete',
    complete: 'complete',
    released: 'released',
};

const STATUS_ORDER = {
    [SLIDE_STATUS.incomplete]: 0,
    [SLIDE_STATUS.complete]: 1,
    [SLIDE_STATUS.released]: 2,
};

export function isQuarantined(item) {
    return item?.girderNetwork?.quarantined === true;
}

/**
 * Published to the network only when both the slide and its case are out of quarantine.
 * Until then, Case Builder may still edit the slide (even if the slide itself was released).
 */
export function isSlidePublished(item, caseQuarantined = false) {
    return !isQuarantined(item) && !caseQuarantined;
}

export function canEditSlide(item, caseQuarantined = false) {
    return !isSlidePublished(item, caseQuarantined);
}

export function getSlideStatus(item) {
    if (!isQuarantined(item)) return SLIDE_STATUS.released;
    const validation = validateItemForApprove(item);
    return validation.isValid ? SLIDE_STATUS.complete : SLIDE_STATUS.incomplete;
}

export function sortSlides(items) {
    return [...(items || [])].sort((a, b) => {
        const statusDiff = STATUS_ORDER[getSlideStatus(a)] - STATUS_ORDER[getSlideStatus(b)];
        if (statusDiff !== 0) return statusDiff;
        return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
}

export function blockNameForItem(item, caseBlocks = []) {
    const blockId = item?.bdsaData?.blockId;
    if (blockId) {
        const block = (caseBlocks || []).find((b) => String(b._id) === String(blockId));
        if (block?.name) return block.name;
    }
    return null;
}

export function stainLabel(item) {
    const stain = item?.bdsaData?.stain;
    if (!stain) return null;
    return stain.abbreviation || stain.displayName || stain.name || stain.schemaStainKey || null;
}

/** Lab / Girder filename (prefer BdsaItem.originalFilename when present). */
export function originalFilenameForItem(item) {
    const bdsa = item?.bdsaData || {};
    return bdsa.originalFilename || item?.name || item?._id || null;
}

/** Network / normalized name from BdsaItem when available. */
export function networkFilenameForItem(item) {
    const bdsa = item?.bdsaData || {};
    return bdsa.networkFilename || null;
}

export function magLabel(item) {
    return item?.bdsaData?.magnification || null;
}

/** Display label for stored mag codes (00x / unknown → Unknown). */
export function formatMagnificationDisplay(magnification) {
    const raw = magnification == null ? '' : String(magnification).trim();
    if (!raw || raw === '—' || raw === '00x' || raw === 'unknown_mag') {
        return 'Unknown';
    }
    return raw;
}

export function recutScanChips(item) {
    const level = Number(item?.bdsaData?.level ?? 1) || 1;
    const scan = Number(item?.bdsaData?.scan ?? 1) || 1;
    const chips = [];
    if (level > 1) chips.push(`Section ${level}`);
    if (scan > 1) chips.push(`Scan ${scan}`);
    return chips;
}

export function regionsMatchBlock(regions, block) {
    const slideRegions = Array.isArray(regions) ? regions : [];
    const blockRegions = Array.isArray(block?.regionDefinitions) ? block.regionDefinitions : [];
    if (slideRegions.length !== blockRegions.length) return false;
    const blockIds = new Set(blockRegions.map((r) => String(r._id)));
    return slideRegions.every((r) => r?._id && blockIds.has(String(r._id)));
}

export function statusLabel(status) {
    if (status === SLIDE_STATUS.incomplete) return 'Incomplete';
    if (status === SLIDE_STATUS.complete) return 'Ready';
    return 'Released';
}

export function countByStatus(items) {
    const counts = {
        [SLIDE_STATUS.incomplete]: 0,
        [SLIDE_STATUS.complete]: 0,
        [SLIDE_STATUS.released]: 0,
    };
    (items || []).forEach((item) => {
        counts[getSlideStatus(item)] += 1;
    });
    return counts;
}

export function defaultStatusTab(counts = {}) {
    if (counts[SLIDE_STATUS.incomplete] > 0) return SLIDE_STATUS.incomplete;
    if (counts[SLIDE_STATUS.complete] > 0) return SLIDE_STATUS.complete;
    if (counts[SLIDE_STATUS.released] > 0) return SLIDE_STATUS.released;
    return SLIDE_STATUS.incomplete;
}

export function hasAmbiguousBlockMatch(item) {
    return item?.bdsaData?.filenameMatchHints?.block?.status === 'ambiguous';
}

export function hasAmbiguousStainMatch(item) {
    return item?.bdsaData?.filenameMatchHints?.stain?.status === 'ambiguous';
}

export function hasAmbiguousFilenameMatch(item) {
    return hasAmbiguousBlockMatch(item) || hasAmbiguousStainMatch(item);
}

export function summarizeFilenameMatchResults(items = []) {
    let ambiguous = 0;
    let autoAssigned = 0;
    (items || []).forEach((item) => {
        const hints = item?.bdsaData?.filenameMatchHints;
        const bdsa = item?.bdsaData || {};
        if (hints?.block?.status === 'ambiguous' || hints?.stain?.status === 'ambiguous') {
            ambiguous += 1;
            return;
        }
        if (bdsa.blockId || bdsa.stain) {
            autoAssigned += 1;
        }
    });
    return { ambiguous, autoAssigned };
}

export function filterSlides(items, { status, query, caseBlocks } = {}) {
    const q = String(query || '').trim().toLowerCase();
    return (items || []).filter((item) => {
        if (status && getSlideStatus(item) !== status) return false;
        if (!q) return true;
        const original = String(originalFilenameForItem(item) || '').toLowerCase();
        if (original.includes(q)) return true;
        const network = String(networkFilenameForItem(item) || '').toLowerCase();
        if (network.includes(q)) return true;
        const name = String(item?.name || '').toLowerCase();
        if (name.includes(q)) return true;
        const block = String(blockNameForItem(item, caseBlocks) || '').toLowerCase();
        if (block.includes(q)) return true;
        const stain = String(stainLabel(item) || '').toLowerCase();
        if (stain.includes(q)) return true;
        return false;
    });
}
